import React, { useState, useEffect } from 'react';
import { createPortal } from 'react-dom';
import { useCurrency } from '../../contexts/CurrencyContext';

// The small sticker meant for the package itself - deliberately compact and separate
// from OrderInvoice.js (the full paperwork). Same portal + print pattern as the invoice
// (see OrderInvoice.js for why: rendering inline inside a long dashboard page and only
// hiding the rest via visibility:hidden leaves blank pages around the actual content).
const ShippingLabel = ({ order, onClose }) => {
  const { formatPrice } = useCurrency();
  const [labelSettings, setLabelSettings] = useState(null);

  useEffect(() => {
    fetch('/api/settings/design')
      .then((res) => res.json())
      .then((data) => {
        if (data.success) setLabelSettings(data.data.design);
      })
      .catch(() => {});
  }, []);

  if (!order) return null;

  const shippingAddress = typeof order.shipping_address === 'string'
    ? JSON.parse(order.shipping_address || '{}')
    : order.shipping_address || {};

  const isCod = ['cod', 'cash_on_delivery'].includes(String(order.payment_method || '').toLowerCase());
  const showLogo = (labelSettings?.label_show_logo ?? 'true') === 'true';
  const showReturnAddress = (labelSettings?.label_show_return_address ?? 'true') === 'true';
  const showCodAmount = (labelSettings?.label_show_cod_amount ?? 'true') === 'true';
  const handlingNote = labelSettings?.label_note || '';
  const labelSize = labelSettings?.label_size || 'thermal_4x6';

  const itemCount = Array.isArray(order.items)
    ? order.items.reduce((sum, item) => sum + (Number(item.quantity) || 0), 0)
    : null;

  const handlePrint = () => window.print();

  return createPortal(
    <div className="label-modal-root fixed inset-0 z-50 flex items-center justify-center p-4">
      <div className="label-modal-backdrop absolute inset-0 bg-black bg-opacity-50" onClick={onClose} />
      <div className="label-print-root relative w-full max-w-md max-h-[90vh] overflow-y-auto rounded-lg bg-white shadow-xl">
        <div className="sticky top-0 bg-white border-b border-gray-200 px-6 py-4 flex justify-between items-center print:hidden">
          <h2 className="text-xl font-bold text-gray-900">Shipping Label</h2>
          <div className="flex gap-3">
            <button
              onClick={handlePrint}
              className="px-4 py-2 bg-indigo-600 text-white rounded-lg hover:bg-indigo-700 transition-colors font-semibold flex items-center gap-2"
            >
              <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17 17h2a2 2 0 002-2v-4a2 2 0 00-2-2H5a2 2 0 00-2 2v4a2 2 0 002 2h2m2 4h6a2 2 0 002-2v-4a2 2 0 00-2-2H9a2 2 0 00-2 2v4a2 2 0 002 2zm8-12V5a2 2 0 00-2-2H9a2 2 0 00-2 2v4h10z" />
              </svg>
              Print Label
            </button>
            <button onClick={onClose} className="px-4 py-2 bg-gray-200 text-gray-700 rounded-lg hover:bg-gray-300 transition-colors font-semibold">
              Close
            </button>
          </div>
        </div>

        {/* Label Content */}
        <div className="label-content p-6 print:p-4 border-2 border-dashed border-gray-300 print:border-solid m-4 print:m-0">
          {showLogo && labelSettings?.invoice_logo && (
            <div className="mb-3 text-center">
              <img src={labelSettings.invoice_logo} alt="" className="max-h-12 mx-auto object-contain" />
            </div>
          )}

          {showReturnAddress && labelSettings?.invoice_company_name && (
            <div className="mb-4 pb-3 border-b border-gray-300 text-xs text-gray-600">
              <p className="font-semibold text-gray-800">From: {labelSettings.invoice_company_name}</p>
              {labelSettings.invoice_company_address && <p>{labelSettings.invoice_company_address}</p>}
              <p>
                {[labelSettings.invoice_company_city, labelSettings.invoice_company_state, labelSettings.invoice_company_zip]
                  .filter(Boolean).join(', ')}
              </p>
              {labelSettings.invoice_company_phone && <p>Ph: {labelSettings.invoice_company_phone}</p>}
            </div>
          )}

          <p className="text-xs uppercase tracking-wide text-gray-500 mb-1">Ship To</p>
          <p className="text-lg font-bold text-gray-900">
            {shippingAddress.first_name || ''} {shippingAddress.last_name || ''}
          </p>
          <p className="text-base text-gray-800 mt-1">{shippingAddress.street || shippingAddress.address || ''}</p>
          <p className="text-base text-gray-800">
            {shippingAddress.city || ''}, {shippingAddress.state || ''} {shippingAddress.zip_code || shippingAddress.zipCode || ''}
          </p>
          <p className="text-base text-gray-800">{shippingAddress.country || ''}</p>
          <p className="text-base font-semibold text-gray-900 mt-1">
            Phone: {order.phone || shippingAddress.phone || 'N/A'}
          </p>

          <div className="mt-4 pt-3 border-t-2 border-gray-800 flex items-center justify-between">
            <div>
              <p className="text-xs uppercase tracking-wide text-gray-500">Order</p>
              <p className="text-xl font-bold text-gray-900">#{order.order_number || order.id}</p>
            </div>
            {itemCount !== null && (
              <div className="text-right">
                <p className="text-xs uppercase tracking-wide text-gray-500">Items</p>
                <p className="text-xl font-bold text-gray-900">{itemCount}</p>
              </div>
            )}
          </div>

          {isCod && showCodAmount ? (
            <div className="mt-4 p-3 bg-yellow-50 border-2 border-yellow-400 rounded-lg text-center">
              <p className="text-xs uppercase tracking-wide text-yellow-800 font-semibold">Collect on Delivery</p>
              <p className="text-2xl font-bold text-yellow-900">{formatPrice(order.total_amount || 0)}</p>
            </div>
          ) : (
            <div className="mt-4 p-2 bg-green-50 border border-green-300 rounded-lg text-center">
              <p className="text-sm font-semibold text-green-800">PREPAID - No collection needed</p>
            </div>
          )}

          {handlingNote && (
            <p className="mt-4 text-center text-sm font-bold text-red-700 uppercase">{handlingNote}</p>
          )}
        </div>
      </div>

      {/* Print Styles */}
      <style>{`
        @media print {
          #root {
            display: none !important;
          }
          body * {
            visibility: hidden !important;
          }
          .label-modal-root,
          .label-modal-root * {
            visibility: visible !important;
          }
          .label-modal-root {
            position: static !important;
            display: block !important;
            padding: 0 !important;
            background: white !important;
          }
          .label-modal-backdrop {
            display: none !important;
          }
          .label-print-root {
            position: static !important;
            inset: auto !important;
            width: 100% !important;
            max-width: 100% !important;
            max-height: none !important;
            overflow: visible !important;
            margin: 0 !important;
            border: none !important;
            border-radius: 0 !important;
            box-shadow: none !important;
          }
          .label-content {
            margin: 0 !important;
            border: none !important;
          }
          .print\\:hidden {
            display: none !important;
          }
          .print\\:p-4 {
            padding: 1rem !important;
          }
          .print\\:m-0 {
            margin: 0 !important;
          }
          .print\\:border-solid {
            border-style: solid !important;
          }
          @page {
            size: ${labelSize === 'a5_half' ? 'A5' : '4in 6in'};
            margin: ${labelSize === 'a5_half' ? '10mm' : '0.15in'};
          }
        }
      `}</style>
    </div>,
    document.body
  );
};

export default ShippingLabel;
